import React, { useCallback, useMemo } from 'react';
import type { ClassNameProp } from '../common-types';
import type { icModel } from 'generated-ts-common';
import { Checkbox } from '../form';

export type StudyCodeListProps = ClassNameProp & {
  availableCodes: icModel.saf.infrastructure.model.svs.GenCode[];
  selectedCodes: icModel.saf.infrastructure.model.svs.GenCode[];
  onChange?(
    selectedCodes: icModel.saf.infrastructure.model.svs.GenCode[]
  ): void;
  labelTitle?: string;
  labelDescription?: string;
  readOnly?: boolean;
};

export function StudyCodeList({
  className = '',
  availableCodes,
  selectedCodes,
  onChange,
  labelTitle,
  labelDescription,
  readOnly,
  ...props
}: Readonly<StudyCodeListProps>): React.JSX.Element {
  const availableCodesSorted = useMemo(
    () => sortCodesByDisplayName([...availableCodes]),
    [availableCodes]
  );

  const selectedCodesAsString = useMemo(
    () => new Set(selectedCodes.map(a => calcUniqueKey(a))),
    [selectedCodes]
  );

  const internalOnChange = useCallback(
    (code: icModel.saf.infrastructure.model.svs.GenCode, checked: boolean) => {
      if (checked) {
        const newCodes = [...selectedCodes, code];
        onChange?.(newCodes);
      } else {
        const newCodes = selectedCodes.filter(
          c => calcUniqueKey(c) !== calcUniqueKey(code)
        );
        onChange?.(newCodes);
      }
    },
    [selectedCodes, onChange]
  );

  const codeControls = useMemo(
    () =>
      availableCodesSorted
        .map(code => {
          const key = calcUniqueKey(code);
          const isChecked = selectedCodesAsString.has(key);
          if (readOnly) {
            return isChecked ? (
              <div key={key} className={'ehs-study-code-list-code'}>
                {code.displayName}
              </div>
            ) : undefined;
          }
          return (
            <Checkbox
              key={key}
              label={code.displayName}
              value={isChecked}
              onChange={checked => internalOnChange(code, checked)}
            />
          );
        })
        .filter(control => control !== undefined),
    [availableCodesSorted, selectedCodesAsString, readOnly, internalOnChange]
  );

  if (codeControls.length === 0) {
    return <></>;
  }

  return (
    <div
      {...props}
      className={`ehs-study-code-list policy-section ${
        readOnly ? 'readOnly' : ''
      } ${className}`.trim()}
    >
      {labelTitle && (
        <h3 className={'ehs-study-code-list-title sh-font font-18'}>
          {labelTitle}
        </h3>
      )}
      {labelDescription && (
        <p className={'font-16 ehs-study-code-list-description'}>
          {labelDescription}
        </p>
      )}
      <div className={'font-16 ehs-study-code-list-codes'}>{codeControls}</div>
    </div>
  );
}

function calcUniqueKey(code: icModel.saf.infrastructure.model.svs.GenCode) {
  return code.value + '^^^&' + code.codingScheme + '&ISO';
}

function getDisplayNameToLowerCase(
  code: icModel.saf.infrastructure.model.svs.GenCode
) {
  return code.displayName.toLowerCase();
}

function sortCodesByDisplayName(
  list: icModel.saf.infrastructure.model.svs.GenCode[]
) {
  return list.sort((c1, c2) => {
    const c1DisplayName = getDisplayNameToLowerCase(c1);
    const c2DisplayName = getDisplayNameToLowerCase(c2);
    if (c1DisplayName < c2DisplayName) {
      return -1;
    }
    if (c1DisplayName > c2DisplayName) {
      return 1;
    }
    return 0;
  });
}
